import { Table, type TableProps } from "@mantine/core";
import type { ReactNode } from "react";

export type AdminTableProps = {
  /**
   * 表格内容最小宽度；视口更窄时 Table.ScrollContainer 出现横向滚动。
   * 宽表建议按列数估算：约 120-160 * 列数。
   */
  readonly minWidth?: number;
  /** 限制高度后出现纵向滚动；与 stickyHeader 搭配表头吸顶 */
  readonly maxHeight?: number | string;
  /**
   * native：浏览器原生滚动条（触控板横滑更稳，Mantine 推荐宽表场景）
   * scrollarea：Mantine ScrollArea 覆盖层滚动条
   * @see https://mantine.dev/core/table/#scroll-container
   */
  readonly scrollType?: "native" | "scrollarea";
  readonly children: ReactNode;
} & Omit<TableProps, "children">;

/**
 * Admin 统一表格外壳：
 * - Table.ScrollContainer(minWidth) 保证横向可滚，避免挤扁列
 * - 默认 striped / highlightOnHover / withTableBorder
 * - maxHeight 时自动 stickyHeader
 */
export function AdminTable(props: AdminTableProps) {
  const {
    minWidth = 960,
    maxHeight,
    scrollType = "native",
    children,
    striped = true,
    highlightOnHover = true,
    withTableBorder = true,
    verticalSpacing = "sm",
    stickyHeader,
    stickyHeaderOffset,
    className,
    ...rest
  } = props;

  const enableSticky = stickyHeader === true || (stickyHeader !== false && maxHeight !== undefined);

  return (
    <Table.ScrollContainer
      minWidth={minWidth}
      {...(scrollType === "native" ? { type: "native" as const } : {})}
      {...(maxHeight !== undefined ? { maxHeight } : {})}
      className="admin-table-scroll"
    >
      <Table
        striped={striped}
        highlightOnHover={highlightOnHover}
        withTableBorder={withTableBorder}
        verticalSpacing={verticalSpacing}
        className={["admin-table", className].filter(Boolean).join(" ")}
        {...(enableSticky ? { stickyHeader: true } : {})}
        {...(stickyHeaderOffset !== undefined ? { stickyHeaderOffset } : {})}
        {...rest}
      >
        {children}
      </Table>
    </Table.ScrollContainer>
  );
}
